import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CodeIcon } from "../../../../../assets/images/icons/CodeIcon";
import LanguagesDropdown, {
	Language,
} from "../../../../../components/common/ApiReferenceDialog/LanguagesDropdown/LanguagesDropdown";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import CopyToClipboardButton from "../../../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { useAppStore } from "../../../../../hooks/useStores";
import { Badge } from "../../../../Fibo/StructuredPrompt/FiboPromptTree/Badge";
import { useVideoEditingPayload } from "../../hooks/useVideoEditingPayload";
import styles from "./APIPayloadPreview.module.scss";
import PayloadCodeEditor from "./PayloadCodeEditor/PayloadCodeEditor";

type ViewMode = "payload" | "code";

const APIPayloadPreview = () => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.apiPayloadPreview" });
	const { videoEditingStore } = useAppStore();
	const [selectedLanguage, setSelectedLanguage] = useState<Language>("python");
	const [viewMode, setViewMode] = useState<ViewMode>("code");
	const selectedTool = videoEditingStore.config.tool;
	const { rawJsonPayload, fullRequestCode } = useVideoEditingPayload(
		selectedTool,
		selectedLanguage,
		videoEditingStore.config,
	);

	const handleLanguageChange = (language: Language) => {
		setSelectedLanguage(language);
		setViewMode("code");
	};

	const displayedCode = useMemo(() => {
		if (!selectedTool) return null;
		return viewMode === "code" ? fullRequestCode : rawJsonPayload;
	}, [viewMode, fullRequestCode, rawJsonPayload, selectedTool]);

	const codeToCopy = useMemo(() => {
		return displayedCode || "";
	}, [displayedCode]);

	const editorLanguage = useMemo((): Language | "json" => {
		if (viewMode === "payload") {
			return "json";
		}
		return selectedLanguage;
	}, [viewMode, selectedLanguage]);

	const hasSelectedTool = !!selectedTool;

	return (
		<Box className={styles.apiPayloadPreview}>
			<Box className={styles.apiPayloadPreviewHeader}>
				<Box className={styles.titleContainer}>
					<Typography className={styles.title}>{t("title")}</Typography>
				</Box>
				<BriaButtonGroup>
					<BriaButton
						buttonType="textSmall"
						className={styles.iconButton}
						title={viewMode === "payload" ? t("showFullRequest") : t("showPayload")}
						onClick={() => setViewMode(viewMode === "payload" ? "code" : "payload")}
						disabled={!hasSelectedTool}
					>
						{viewMode === "payload" ? <CodeIcon /> : <Badge />}
					</BriaButton>

					<CopyToClipboardButton
						buttonType="textSmall"
						textToCopy={codeToCopy}
						className={styles.iconButton}
						tooltipText={t("copyToClipboard")}
						disabled={!displayedCode || !hasSelectedTool}
					/>
					<LanguagesDropdown selectedLanguage={selectedLanguage} setSelectedLanguage={handleLanguageChange} />
				</BriaButtonGroup>
			</Box>

			<LoadingPlaceholder isLoading={false} className={clsx({ [styles.loadingPlaceholder]: false })}>
				<PayloadCodeEditor code={displayedCode} language={editorLanguage} />
			</LoadingPlaceholder>
		</Box>
	);
};

export default observer(APIPayloadPreview);
